import moment from 'moment';

import logger from 'js/app/loggerSingleton';
import store from 'js/lib/coursera.store';
import { stringKeyToTuple } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import { COURSERA_PLUS_SUBSCRIPTION } from 'bundles/payments-common/common/ProductType';

export type AbandonCartPromoData = {
  userId?: number;
  expiresAt?: number;
  promoCode: string;
  productType?: string;
  productId?: string;
  productSku?: string;
};

const LOCAL_STORAGE_KEY = 'abandonedCartPromoData';

export const getPromotionDataFromQuery = (query: {
  userId?: string;
  timestamp?: string;
  edocomorp?: string; // Make promo code more cryptic since it's user facing
  productSku?: string;
  productId?: string;
}): AbandonCartPromoData | null => {
  if (!query || !query.edocomorp || !query.userId) {
    return null;
  }
  const { productSku, edocomorp, productId, timestamp: timestampQuery } = query;
  const productType = productSku && stringKeyToTuple(productSku)[0];
  const shouldSkipTimestampCheck = productType === COURSERA_PLUS_SUBSCRIPTION;

  // Non-Coursera Plus abandoned cart links still require timestamp.
  if (!shouldSkipTimestampCheck && !timestampQuery) {
    return null;
  }

  const userId = parseInt(query.userId, 10);
  const expiresAt = timestampQuery ? moment(parseInt(timestampQuery, 10)).add(14, 'days').valueOf() : undefined;

  return {
    userId,
    expiresAt,
    promoCode: edocomorp,
    productType,
    productId,
    productSku,
  };
};

export const clearAbandonedCartPromotionFromStorage = () => {
  store.remove(LOCAL_STORAGE_KEY);
};

export const saveAbandonedCartPromoDataToStorage = (promoData: AbandonCartPromoData) => {
  if (typeof window === 'undefined') {
    logger.error('Attempted to save abandoned cart promo to local storage in a server context');
    return;
  }

  const base64String = btoa(JSON.stringify(promoData));
  store.set(LOCAL_STORAGE_KEY, base64String);
};

export const loadAbandonedCartPromoDataFromStorage = (): AbandonCartPromoData | typeof undefined => {
  const base64String = store.get(LOCAL_STORAGE_KEY);
  try {
    return JSON.parse(atob(base64String));
  } catch {
    return undefined;
  }
};

// check if the current s12n or VC abandoned cart promotion is valid for the current user
export const isValidAbandonedCartPromotion = (userId: number, expiresAt: number) => {
  return user.get().id === userId && new Date(expiresAt) > new Date();
};
